// Shared read data-layer client for the SPA (RFC-0009 Task 1.1; ADR-0007).
//
// Repo convention: plain same-origin `fetch` (the SPA is served from the API's
// own origin — no CORS, no axios). This generalizes the typed-error pattern in
// lib/sync-api.ts / lib/auth-api.ts for the RFC-0006 *read* endpoints; the
// TanStack Query hooks in lib/queries/* wrap it (`fetch` remains the transport).
//
// Error classification (RFC-0009 §1/§4):
//   - a G13 envelope `{error:{code,message}}` on any non-2xx → `ApiError`
//     (code + message preserved, plus the numeric `status`);
//   - a 401 whose envelope `code === 'unauthorized'` → `UnauthorizedError`
//     (a distinct subclass Phase 2's interceptor keys on) — never a bare ApiError;
//   - a transport failure (offline / DNS / abort) → `NetworkError`.
//
// A module-level unauthorized handler lets AuthProvider (Phase 2) register the
// global 401 redirect. `ApiClient` invokes it on `UnauthorizedError` *before*
// rethrowing so the query still settles as an error (and does not retry — see
// `shouldRetryRead`).

/** Shape of the shared G13 error envelope returned by the API (PD-0005). */
interface G13ErrorBody {
  error?: { code?: string; message?: string };
}

/**
 * A non-2xx response carrying the G13 envelope. `status` is the HTTP status and
 * `code` is the envelope's machine-readable code (e.g. `not_found`,
 * `invalid_query`). 4xx are caller errors (never retried); 5xx are transient.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * A 401 with envelope `code: "unauthorized"` (RFC-0005's `requireSession` body).
 * The Phase 2 interceptor redirects to `/login?return_to=…` on this type; it
 * must never surface as a generic `ApiError`.
 */
export class UnauthorizedError extends ApiError {
  constructor(message: string) {
    super(401, 'unauthorized', message);
    this.name = 'UnauthorizedError';
  }
}

/** A transport-level failure (no HTTP response): offline, DNS, aborted, etc. */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NetworkError';
  }
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Register (or clear, with `null`) the global 401 handler. AuthProvider calls
 * this on mount with `handleUnauthorized` and clears it on unmount (RFC-0009 B4).
 */
export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  unauthorizedHandler = fn;
}

export type QueryParamValue = string | number | boolean | string[] | undefined | null;
export type QueryParams = Record<string, QueryParamValue>;

/**
 * Serialize params into a query string: `undefined`/`null`/empty values are
 * dropped and arrays become repeated keys (`?tag=a&tag=b`) to match RFC-0006's
 * `toStringArray` parsing.
 */
function buildQueryString(params?: QueryParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && item !== '') sp.append(key, String(item));
      }
    } else if (typeof value === 'string') {
      if (value !== '') sp.append(key, value);
    } else {
      // number | boolean → 'true'/'false' or the decimal string.
      sp.append(key, String(value));
    }
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/** Optional per-request controls for `apiGet` (RFC-0011 Task 1.1). */
export interface ApiGetOptions {
  /**
   * Abort signal forwarded to the underlying `fetch`. TanStack Query passes its
   * per-execution `signal` here so a superseded query (queryKey change) cancels
   * its in-flight request (RFC-0011 `useSearch` debounce → cancellation).
   */
  signal?: AbortSignal;
}

/**
 * GET a same-origin `/api/*` JSON resource with cookies attached. Resolves with
 * the parsed body on 2xx; rejects with `UnauthorizedError` (401 unauthorized),
 * `ApiError` (any other non-2xx), or `NetworkError` (transport failure).
 *
 * `options.signal` is optional and backward-compatible: existing two-arg callers
 * (`lib/queries/*`) pass nothing and are unaffected.
 */
export async function apiGet<T>(
  path: string,
  params?: QueryParams,
  options?: ApiGetOptions,
): Promise<T> {
  const url = `${path}${buildQueryString(params)}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
      signal: options?.signal,
    });
  } catch (err) {
    // An aborted request throws an `AbortError` — a `DOMException`, which is NOT
    // an `Error` subclass in every runtime, so match on the `name` (or the
    // signal's own aborted flag) rather than `instanceof Error`. Rethrow it
    // as-is: TanStack Query recognizes an AbortError as a cancellation and
    // silently discards the result on a queryKey change. Wrapping it as a
    // `NetworkError` would instead satisfy `shouldRetryRead` and (wrongly) retry
    // a superseded request. Every other transport failure → `NetworkError`.
    const name = (err as { name?: unknown } | null)?.name;
    if (name === 'AbortError' || options?.signal?.aborted) throw err;
    throw new NetworkError(
      'Could not reach the server. Check your connection and try again.',
    );
  }

  if (res.ok) {
    return (await res.json()) as T;
  }

  // Non-2xx: read the G13 envelope for a machine code + human message.
  let body: G13ErrorBody | null = null;
  try {
    body = (await res.json()) as G13ErrorBody;
  } catch {
    body = null;
  }
  const code = body?.error?.code ?? 'error';
  const message = body?.error?.message ?? `Request failed with status ${res.status}.`;

  if (res.status === 401 && code === 'unauthorized') {
    const err = new UnauthorizedError(message);
    // Fire the global interceptor (Phase 2) before rethrowing so the redirect
    // starts immediately; the query still settles as an error and won't retry.
    unauthorizedHandler?.();
    throw err;
  }

  throw new ApiError(res.status, code, message);
}

/**
 * Send a same-origin `/api/*` mutation (RFC-0018 §Web) — the SPA's first WRITE
 * transport. Mirrors `apiGet`'s error classification (`UnauthorizedError` on a 401
 * `unauthorized` envelope, `ApiError` on any other non-2xx, `NetworkError` on a
 * transport failure) and, like it, sends cookies (`credentials: 'include'`) on the
 * same origin the SPA is served from — so the Kratos session rides along and
 * `enforceSameOrigin` sees a same-origin `Origin`/`Referer`.
 *
 * 204-aware: a `POST`/`DELETE /api/favorites` returns `204 No Content`, so this
 * NEVER calls `res.json()` on an empty body (which would throw). A 2xx with a JSON
 * body (future write endpoints) is parsed and returned; a 204/empty body resolves
 * to `undefined`.
 */
export async function apiSend<T = void>(
  method: 'POST' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new NetworkError(
      'Could not reach the server. Check your connection and try again.',
    );
  }

  if (res.ok) {
    // 204 (or any empty body): resolve without parsing — `res.json()` on an empty
    // body rejects. Only parse when there is a body to parse.
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? (JSON.parse(text) as T) : (undefined as T));
  }

  // Non-2xx: read the G13 envelope for a machine code + human message (same
  // classification as apiGet).
  let errBody: G13ErrorBody | null = null;
  try {
    errBody = (await res.json()) as G13ErrorBody;
  } catch {
    errBody = null;
  }
  const code = errBody?.error?.code ?? 'error';
  const message = errBody?.error?.message ?? `Request failed with status ${res.status}.`;

  if (res.status === 401 && code === 'unauthorized') {
    const err = new UnauthorizedError(message);
    unauthorizedHandler?.();
    throw err;
  }

  throw new ApiError(res.status, code, message);
}

/**
 * TanStack Query `retry` predicate for the read layer (RFC-0009 §1/§4): never
 * retry a 401 or any other 4xx `ApiError` (so §4's redirect fires on the first
 * failure); retry only transport failures and 5xx, capped at two attempts.
 */
export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  if (error instanceof UnauthorizedError) return false;
  if (error instanceof ApiError) {
    // 4xx: caller error — do not retry. 5xx: transient — retry.
    return error.status >= 500 && failureCount < 2;
  }
  // NetworkError / unknown: transient.
  return failureCount < 2;
}
